import { useQuery, queryCache } from "react-query"
import { ApplicationDispatch } from '../store/application/types';
import { fetchData, postData } from './fetch';
import { useDispatch, useSelector } from 'react-redux';
import { IEmployeeRole } from "../Interfaces/Admin/IEmployeeRole";
import { useCallback } from "react";
import { AppState } from '../store/rootReducer';
import { includes } from 'ramda';
import { hasValue } from "@amsanalytics/react-shared-library";

export const useEmployeeRoles = () => {
  const dispatch = useDispatch<ApplicationDispatch>();

  const getEmployeeRoles = async (key: string) => {
    const url = `api/EmployeeRole/List`;
    const response = await fetchData(url, dispatch) as IEmployeeRole[];
    return response;
  };

  return useQuery(
    ['EmployeeRoles'],
    getEmployeeRoles,
    {
    }
  );
}

// export const useEmployeeRole = (ids?: string[]) => {
//   const dispatch = useDispatch<ApplicationDispatch>();

//   const getEmployeeRole = async (key: string, id?: string) => {
//     if (id) {
//       const url = `api/EmployeeRole/${id}`;
//       const response = await fetchData(url, dispatch) as IEmployeeRole;
//       return response;
//     } else {
//       return undefined;
//     }
//   };

//   return useQuery(
//     ['EmployeeRoles', id],
//     getEmployeeRole,
//     {
//       refetchOnWindowFocus: false,
//     }
//   );
// }

export const updateEmployeeRole = async (employeeRole: IEmployeeRole) => {
  const url = `api/EmployeeRole/Update`;
  const response = await postData(url, employeeRole) as {};
  await queryCache.refetchQueries(['EmployeeRoles', employeeRole.id]);
  return response;
}

export const EmployeeRoleModules = [
  {
    module: "applicant",
    fields: [
      "firstName",
      "middleName",
      "lastName",
      "suffix",
      "dlnumber",
      "dlstateId",
      "ssn",
      "dateOfBirth",
      "numberOfDependents",
      "note",
      "housingStatusId",
      "maritalStatusId",
    ]
  }, {
    module: "customerAddress",
    fields: [
      "street1",
      "street2",
      "city",
      "country",
      "county",
      "township",
      "stateId",
      "zipCode",
      "zipCodeExt",
      "landlordName",
      "landlordPhone",
      "rentMortgagePayment",
    ]
  },
  {
    module: "customerEmail",
    fields: [
      "emailAddress",
      "doNotEmail",
      "emailType",
    ]
  },
  {
    module: "customerPhone",
    fields: [
      "phoneNumber",
      "doNotText",
      "doNotCall",
      "extension",
      "type",
      "phoneBilledTo",
    ]
  },
  {
    module: "customerEmployment",
    fields: [
      "companyName",
      "supervisor",
      "title",
      "verified",
      "income",
      "phone",
      "streetAddress",
      "city",
      "stateId",
      "zipCode",
      "primaryEmployment",
      "isCurrent",
      "hoursWorked",
      "hourlyPay",
      "incomeTypeId",
      "incomePeriodId",
      "jobCodeId",
      "jobTypeId",
      "workShiftId",
    ]
  },
  {
    module: "customerIncomeDebt",
    fields: [
      "supportPayments",
      "supportPaymentAmount",
      "bankruptcyFiled",
      "bankruptcyChapter",
      "bankruptcyComments",
      "dischargeDate",
      "reposessed",
      "repoDate",
      "repoType",
      "priorVehicleAmountBorrowed",
      "priorVehicleAmountOwed",
      "priorVehicleLienHolder",
      "bankName",
      "bankCity",
      "checkingBalance",
      "savingsBalance",
      "otherMonthlyIncome",
      "otherIncomeSource",
      "utilitiesPayment",
      "childCarePayment",
      "tvpayment",
      "downpayment",
      "bureauOtherDebt",
      "tradeVehicle",
      "tradeYear",
      "tradeMake",
      "tradeModel",
    ]
  },
  {
    module: "Bureau",
  },
  {
    module: "Vehicle",
  },
  {
    module: "Custom Fields",
  },
  {
    module: "Quote",
  },
  {
    module: "Budget",
  },
  {
    module: "Documents",
  },
  {
    module: "Stipulations",
  }
]

const doNotAllow = () => false;

export const useEmployeeRolePermissions = (): [
  (moduleName: string, fieldName?: string) => boolean,
  (moduleName: string, fieldName?: string) => boolean,
  (moduleName: string, fieldName: string) => boolean,
  (statusId: string, action: string) => boolean,
] => {
  const loggedInUser = useSelector((appState: AppState) => appState.applications.loggedInUser);
  const allEmployeeRoles = useSelector((appState: AppState) => appState.applications.employeeRoles);
  const appStatus = useSelector((appState: AppState) => appState.applications.editingApp);
  const employeeRoleIds = loggedInUser?.employee?.employeeEmployeeRoles?.map(eer => eer.employeeRoleId) ?? [];
  // const { data: employeeRole } = useEmployeeRole(employeeRoleIds);

  const canView = useCallback((moduleName: string, fieldName?: string) => {
    let canView = false;
    if (allEmployeeRoles) {
      const employeeRoles = allEmployeeRoles.filter(er => includes(er.id, employeeRoleIds));
      employeeRoles.forEach(employeeRole => {
        if (!canView) {
          const rolePermissions = JSON.parse(employeeRole.configurationJson);
          const module = rolePermissions.Modules?.[moduleName];
          // const module = employeeRole.configurationJson?.modules?.[moduleName];
          if (module?.canView || module?.canEdit || module?.CanView || module?.CanEdit) { // Check module access.
            canView = true;
          } else if (fieldName) { // Check the specific field access.
            canView = module?.fields?.[fieldName]?.canView || module?.fields?.[fieldName]?.canEdit || module?.fields?.[fieldName]?.CanView || module?.fields?.[fieldName]?.CanEdit || false;
          } else if (module?.fields) { // Check if any field has access for the module.
            for (const f in (module?.fields || {})) {
              if (module.fields[f].canView || module.fields[f].canEdit || module.fields[f].CanView || module.fields[f].CanEdit) {
                canView = true;
              }
            }
          }
        }
      });
    }
    return canView;
  }, [allEmployeeRoles]);

  const canEdit = useCallback((moduleName: string, fieldName?: string) => {
    let canEdit = false;
    if (allEmployeeRoles) {
      const employeeRoles = allEmployeeRoles.filter(er => includes(er.id, employeeRoleIds));
      employeeRoles.forEach(employeeRole => {
        if (!canEdit) {
          const rolePermissions = JSON.parse(employeeRole.configurationJson);
          const module = rolePermissions.Modules?.[moduleName];
          if (module?.canEdit || module?.CanEdit) { // Check module access.
            canEdit = true;
          } else if (fieldName) { // Check the specific field access.
            canEdit = module?.fields?.[fieldName]?.canEdit || module?.fields?.[fieldName]?.CanEdit || false;
          } else if (module?.fields) { // Check if any field has access for the module.
            for (const f in (module?.fields || {})) {
              if (module.fields[f].canEdit || module.fields[f].CanEdit) {
                canEdit = true;
              }
            }
          }
        }
      });
    }
    return canEdit; // Do not allow access by default.
  }, [allEmployeeRoles]);

  const canAct = useCallback((moduleName: string, fieldName: string) => {
    let canAct = false;
    if (allEmployeeRoles) {
      const employeeRoles = allEmployeeRoles.filter(er => includes(er.id, employeeRoleIds));
      employeeRoles.forEach(employeeRole => {
        if (!canAct) {
          const dynamicModules = hasValue(employeeRole.configurationJson) ? JSON.parse(employeeRole.configurationJson) : undefined;
          const module = dynamicModules && dynamicModules[moduleName];
          if (module) {
            canAct = module[fieldName] || false;
          }
        }
      });
    }
    return canAct;
  }, [allEmployeeRoles]);

  const canActInStatus = useCallback((statusId: string, action: string) => {
    let canActInStatus = false;
    if (allEmployeeRoles) {
      const employeeRoles = allEmployeeRoles.filter(er => includes(er.id, employeeRoleIds));
      employeeRoles.forEach(employeeRole => {
        if (canActInStatus === false && appStatus !== undefined) {
          const dynamicModules = hasValue(employeeRole.configurationJson) ? JSON.parse(employeeRole.configurationJson) : undefined;
          const module = dynamicModules && dynamicModules["ActionInStatus"];
          // Default to true in this case unless explicitly denied
          if (module && module[statusId.toLowerCase()]) {
            canActInStatus = module[statusId.toLowerCase()][action];
          }
        }
      });
    }
    return canActInStatus;
  }, [allEmployeeRoles, appStatus]);

  if (!allEmployeeRoles || allEmployeeRoles.length === 0) {
    return [doNotAllow, doNotAllow, doNotAllow, doNotAllow];
  } else {
    return [canView, canEdit, canAct, canActInStatus];
  }
}
